import { ProgramChips, taskPrograms } from '@/lib/programs'
/** `preview` keeps the editable layout but disables all task mutations.
 * Execution materials: starting inputs, recording, final work, and optional tracking.
 * Each slot accepts up to ten files. A continuation uses its parent's final work as input.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type { FileItem, Role, Task, TaskDetail } from '@/lib/types'
import { FileDrop, MAX_FILES_PER_SLOT } from './FileDrop'
import { FileList } from './FileList'
import { Fold } from './Section'

interface Props { task: Task; parent?: Task | null; files: FileItem[]; editable: boolean; onChanged: () => void; onSaved?: (d: TaskDetail) => void; viewer?: Role; onProgramsDirty?: (dirty: boolean) => void; onProgramsChange?: (programs: string[]) => void; downloadable?: boolean; preview?: boolean; scientific?: boolean }

const uploaded = (list: FileItem[]) => list.some((f) => f.status === 'uploaded')

export function SubmissionPanel({ task, parent, files, editable, onChanged, onSaved, viewer = 'sme', onProgramsDirty, onProgramsChange, downloadable = true, preview = false, scientific = false }: Props) {
  const initial = files.filter((f) => f.kind === 'initial_state')
  const video = files.filter((f) => f.kind === 'video')
  const final = files.filter(f => f.kind === 'final_output')
  const state = files.filter((f) => f.kind === 'state')
  const startingPoint = task.brief?.starting_point?.trim()
  const base = viewer === 'sme' ? '/sme/tasks' : '/eng/tasks'
  // A continuation task starts from its parent's outcome, so the declaration does not apply there (workflow.py agrees).
  const emptyStart = Boolean(task.submission?.empty_start) && !parent
  // The PUT already returns the task detail; refetch only when nobody consumes it.
  const saved = (d: TaskDetail) => { if (onSaved) onSaved(d); else onChanged() }
  return (
    <div className="submission">
      {parent && (
        <div className="note info" style={{ marginBottom: '1rem' }}>
          <strong>The execution here should be a continuation of the execution of the task <Link to={`${base}/${parent.task_id}`}>{parent.title}</Link>.</strong>
          {' '}Start from the exact outcome of that execution, like two tasks in the same project.
        </div>
      )}
      <ExecutionPrograms key={task.task_id} task={task} editable={editable} onSaved={saved} onDirty={onProgramsDirty} onChange={onProgramsChange} preview={preview} />
      <section className="execution-materials" data-tour="execution-materials">
      <header><h2>Execution materials</h2><p className="muted small">{scientific ? 'Upload the input data and any result files. Recordings are optional.' : 'Include the inputs and your completed work. Screen recordings and backend tracking are optional.'}</p></header>
      <div className="upload-slots">
        <Slot label="Starting materials" hint="Files needed before you start." files={initial} kind="initial_state" {...{ task, editable, onChanged, downloadable, preview }}
          waived={emptyStart && !scientific}
          note={editable && !parent ? undefined  // the checkbox below carries the explanation
            : emptyStart ? <>This task starts from an empty file, so there are no starting files here.</>
            : <>Upload the files needed <strong>before you start</strong>. They should match the starting point in your brief{startingPoint ? <>: <q>{startingPoint}</q></> : '.'}</>}
          extra={!scientific && (editable || emptyStart) && !parent ? <EmptyStart task={task} editable={editable} viewer={viewer} hasInitial={uploaded(initial)} onSaved={saved} preview={preview} /> : undefined} />
        <Slot optional label="Screen recording" hint="If you have a recording, include the full execution." files={video} kind="video" {...{ task, editable, onChanged, downloadable, preview }} />
        <Slot optional={scientific} label="Final files" hint="Upload your completed files or an archive." files={final} kind="final_output" {...{ task, editable, onChanged, downloadable, preview }} />
      </div>
      </section>
      <Fold title="Backend tracking" count="optional"><Slot optional label="Tracking export" hint="Include a tracking export if you have one. It does not replace final files." files={state} kind="state" {...{ task, editable, onChanged, downloadable, preview }} /></Fold>
      <Notes scientific={scientific} viewer={viewer} task={task} editable={editable} onSaved={saved} preview={preview} />
    </div>
  )
}

function Slot({ task, label, hint, note, extra, files, kind, editable, onChanged, waived = false, optional = false, downloadable = true, preview = false }: { task: Task; label: string; hint: string; note?: ReactNode; extra?: ReactNode; files: FileItem[]; kind: 'video' | 'state' | 'initial_state' | 'final_output'; editable: boolean; onChanged: () => void; waived?: boolean; optional?: boolean; downloadable?: boolean; preview?: boolean }) {
  const ok = uploaded(files)
  const remaining = MAX_FILES_PER_SLOT - files.length
  const canUpload = editable && !waived
  const chip = ok ? <span className="chip ok">Uploaded</span> : (waived || optional) ? <span className="chip neutral">{optional ? 'Optional' : 'Blank start'}</span> : <span className="chip neutral">Required</span>
  return (
    <div className="upload-slot">
      <div className="upload-slot-heading"><div className="head"><span>{label}</span>{chip}</div><p className="small muted">{hint}</p></div>
      <div className="upload-slot-body">
      {note && <p className="small slot-note">{note}</p>}
      {extra}
      <FileList files={files} canDelete={editable} onChanged={onChanged} downloadable={downloadable} disabled={preview} />
      {canUpload && remaining > 0 && <FileDrop disabled={preview} taskId={task.task_id} meta={{ kind }} label={`Upload the ${label.toLowerCase()}`} types={kind === 'video' ? 'MP4 or WebM recommended' : undefined} remaining={remaining} onUploaded={onChanged} />}
      {canUpload && remaining <= 0 && <p className="muted small">This slot is full ({MAX_FILES_PER_SLOT} files). Remove one to upload another.</p>}
      {!editable && !waived && files.length === 0 && <p className="muted small">Not uploaded.</p>}
      </div>
    </div>
  )
}

/**
 * "Empty starting file": the task begins from a blank document. The tracker cannot export a
 * document without checkpoints, so the expert declares it instead of uploading an initial state.
 * Saved at once through the submission route; the forward blocker follows the flag. An uploaded
 * initial state contradicts the declaration, so the box is locked until those files are removed.
 */
function EmptyStart({ task, editable, viewer, hasInitial, onSaved, preview = false }: { task: Task; editable: boolean; viewer: Role; hasInitial: boolean; onSaved: (d: TaskDetail) => void; preview?: boolean }) {
  const saved = Boolean(task.submission?.empty_start)
  // Shown at once; the saved value takes over as soon as the task reloads with the same answer.
  const [local, setLocal] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { if (local !== null && local === saved) setLocal(null) }, [local, saved])
  const checked = local ?? saved
  async function toggle(next: boolean) {
    if (preview) return
    setLocal(next); setBusy(true); setErr(null)
    try { onSaved(await api.put<TaskDetail>(`/${viewer === 'sme' ? 'sme/' : ''}tasks/${task.task_id}/submission`, { revision: task.revision ?? 0, empty_start: next })) }
    catch (e) { setLocal(null); setErr(e instanceof ApiError ? e.message : (e as Error).message) }
    finally { setBusy(false) }
  }
  if (!editable) {
    if (!saved) return null
    return <p className="small slot-note"><strong>Empty starting file</strong> {viewer === 'sme' ? 'declared: this task started from a blank document.' : 'declared by the expert.'}</p>
  }
  const locked = hasInitial && !checked
  return (
    <div className="empty-start">
      <label className="check">
        <input type="checkbox" checked={checked} disabled={preview || busy || locked} onChange={(e) => { void toggle(e.target.checked) }} />
        <span>Start from a blank document</span>
      </label>
      {locked && <p className="small muted">Remove the starting files to use a blank start.</p>}
      {checked && hasInitial && <p className="small err">Remove the uploaded initial state or untick this box before submitting.</p>}
      {err && <p className="small err">{err}</p>}
    </div>
  )
}

function Notes({ task, editable, onSaved, viewer = 'sme', preview = false, scientific = false }: { scientific?: boolean; viewer?: Role; task: Task; editable: boolean; onSaved: (d: TaskDetail) => void; preview?: boolean }) {
  const sub = task.submission ?? {}
  const [notes, setNotes] = useState(sub.notes ?? '')
  const [alt, setAlt] = useState(sub.alternative_approaches ?? '')
  const [msg, setMsg] = useState<string | null>(null)
  useEffect(() => { setNotes(sub.notes ?? ''); setAlt(sub.alternative_approaches ?? '') }, [sub.notes, sub.alternative_approaches])
  const has = Boolean(sub.notes || sub.alternative_approaches)
  if (!editable && !has) return null
  const dirty = notes !== (sub.notes ?? '') || alt !== (sub.alternative_approaches ?? '')

  async function save() {
    if (preview) return
    try {
      onSaved(await api.put<TaskDetail>(`/${viewer === 'sme' ? 'sme/' : ''}tasks/${task.task_id}/submission`, { revision: task.revision ?? 0, notes, alternative_approaches: alt })); setMsg('Saved.')
    } catch (e) { setMsg(e instanceof ApiError ? e.message : (e as Error).message) }
  }

  return (
    <div className="submission-notes">
      <Fold title="Notes for the developer" count={has ? undefined : 'optional'}>
        {editable ? (
          <div>
            <label className="field"><span className="small">{scientific ? 'Explain how you checked the result. Note which rubric criteria should pass or fail.' : 'What you inspected, decided and checked; anything in the reviewed task that does not match your judgment'}</span><textarea readOnly={preview} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
            <label className="field"><span className="small">{scientific ? 'Describe another valid result or method.' : 'A materially different approach or result that should also be accepted'}</span><textarea readOnly={preview} value={alt} onChange={(e) => setAlt(e.target.value)} style={{ minHeight: 60 }} /></label>
            <div className="row"><button type="button" className="sm" disabled={preview || !dirty} onClick={save}>Save notes</button>{msg && <span className="small muted">{msg}</span>}</div>
          </div>
        ) : (
          <dl className="kv">
            {sub.notes && <div><dt>Notes</dt><dd>{sub.notes}</dd></div>}
            {sub.alternative_approaches && <div><dt>Alternative approaches</dt><dd>{sub.alternative_approaches}</dd></div>}
          </dl>
        )}
      </Fold>
    </div>
  )
}


function ExecutionPrograms({ task, editable, onSaved, onDirty, onChange, preview = false }: { task: Task; editable: boolean; onSaved: (d: TaskDetail) => void; onDirty?: (dirty: boolean) => void; onChange?: (programs: string[]) => void; preview?: boolean }) {
  const [programs, setPrograms] = useState(taskPrograms(task))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const savedPrograms = JSON.stringify(taskPrograms(task))
  useEffect(() => { setPrograms(JSON.parse(savedPrograms) as string[]) }, [savedPrograms])
  const dirty = JSON.stringify(programs) !== JSON.stringify(taskPrograms(task))
  useEffect(() => { onDirty?.(editable && (dirty || busy)) }, [dirty, busy, editable, onDirty])
  // Keep coach context current, including an empty selection and values normalized after saving.
  useEffect(() => { onChange?.(programs) }, [programs, onChange])
  async function save() {
    if (preview) return
    setBusy(true); setError('')
    try { onSaved(await api.put<TaskDetail>(`/tasks/${task.task_id}/content`, { revision: task.revision ?? 0, programs })) }
    catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  if (!editable) return null
  return <div className="execution-programs" data-tour="execution-programs"><div className="execution-programs-head"><div><h2>Programs used</h2><p className="small muted">Confirm the programs needed for this task.</p></div>
    <button className="sm" type="button" disabled={preview || !dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save programs'}</button></div>
    <ProgramChips value={programs} onChange={setPrograms} disabled={preview || busy} />
    {error && <p className="note err" role="alert">{error}</p>}
  </div>
}
